import { FC, PropsWithChildren, useEffect, useRef, useState } from "react";
import { io, Socket } from "socket.io-client";
import { setUpSocketIo } from "@/services/socketIo";
import {
  SocketIoMessage,
  UpdateActionHistory,
} from "@/models/socketIo/socketIo";
import { useGameDataContext } from "./GameDataContext";
import { UpdateAction } from "@/models/socketIo/updateAction";
import {
  buildGameState,
  gameState,
  reBuildGameState,
  updateGameState,
} from "@/services/gameState";
import { useSearchParams } from "react-router-dom";
import { useTabManagement } from "./TabManagementContext";
import { getActionHistory } from "@/services/samsService";
import { InitializeMatchPayload } from "@/models/socketIo/initializeMatchPayload";

const SocketIoWrapper: FC<PropsWithChildren> = ({ children }) => {
  const [params] = useSearchParams();

  const { game, updateGameNumber, updateLocation, updateScoresheetPdfUrl } =
    useGameDataContext();
  const setConnectionStatus = gameState("setConnectionStatus");
  const setLatency = gameState("setLatency");
  const lastAction = gameState("lastAction");
  const matchNumber = gameState("matchNumber");
  const location = gameState("location");
  const scoresheetPdfUrl = gameState("scoresheetPdfUrl");

  const { isTabActive } = useTabManagement();

  const socketIo = useRef<Socket>(undefined);

  const [pingInterval, setPingInterval] = useState<number>();
  const [lastPing, setLastPing] = useState<number>();
  const [lastHeartbeat, setLastHeartbeat] = useState<number>();
  const [timeBetweenPings, setTimeBetweenPings] = useState<number>();
  const [socketIoConnected, setSocketIoConnected] = useState(false);
  let interval: NodeJS.Timeout;

  const updateLastPing = () => {
    setLastPing((prev) => {
      const now = Date.now();
      setTimeBetweenPings(now - (prev || now));
      return now;
    });
  };

  useEffect(() => {
    if (game?.samsId && !socketIo.current) {
      const ioSocket = io(import.meta.env.VITE_BACKEND_BASE_URL + "/matches", {
        path: "/socket/socket.io",
        reconnection: true,
      });

      if (ioSocket) {
        ioSocket.connect();
        socketIo.current = ioSocket;

        setUpSocketIo({
          socketIo: ioSocket,
          samsId: game.samsId,
          samsScope: game.scope,
          onData: consumeSocketIoMessage,
          onHeartbeat: () => setLastHeartbeat(Date.now()),
          onPing: () => updateLastPing(),
          onHandshake: (pingInterval) => {
            updateLastPing();
            setPingInterval(pingInterval);
          },
          onConnect: () => {
            getActionHistory({
              matchId: game.samsId,
              onData: consumeSocketIoMessage,
              limitAction: params.get("limitAction"),
              samsScope: game.scope,
            });
            setSocketIoConnected(true);
          },
          onConnectError: (e) => {
            setSocketIoConnected(false);
            console.error(e);
          },
          onDisconnect: (r, e) => {
            setSocketIoConnected(false);
            switch (r) {
              case "io client disconnect":
                console.debug("disconnecting because of client request");
                break;
              default:
                console.error(r, e);
                break;
            }
          },
        });
      }
    }
    return () => {
      if (socketIo?.current?.connected) {
        console.debug("Closing socket (GameStateContext)");
        socketIo.current?.close();
        setConnectionStatus("lost");
        socketIo.current = undefined;
      }
    };
  }, [game?.samsId, socketIo]);

  useEffect(() => {
    if (socketIo.current?.connected && game?.samsId) {
      if (isTabActive) {
        // TODO
        //  pullActionHistory(socketIo.current, game?.samsId);
      } else {
        setConnectionStatus("paused");
      }
    }
  }, [isTabActive]);

  useEffect(() => {
    if (game?.samsId) {
      if (location?.name && location.address.city) {
        updateLocation(
          `${location.name}, ${location.address.city}`,
          game.samsId,
        );
      }
      if (matchNumber) {
        updateGameNumber(matchNumber.toString(), game.samsId);
      }
      if (scoresheetPdfUrl) {
        updateScoresheetPdfUrl(scoresheetPdfUrl, game.samsId);
      }
    }
  }, [location, matchNumber, scoresheetPdfUrl, game?.samsId]);

  useEffect(() => {
    if (pingInterval && lastHeartbeat) {
      const checkConnectionStatus = () => {
        const timeSinceLastHeartbeat = Date.now() - lastHeartbeat;
        const gracePeriod = 1000;
        const latency = timeBetweenPings
          ? timeBetweenPings - pingInterval
          : null;
        if (socketIoConnected) {
          if (timeSinceLastHeartbeat > pingInterval + gracePeriod) {
            setConnectionStatus("loosing");
            setLatency(null);
          } else if (isTabActive) {
            setConnectionStatus("connected");
            setLatency(latency);
          }
        } else {
          setConnectionStatus("lost");
          setLatency(null);
        }
      };

      interval = setInterval(checkConnectionStatus, 1000);

      return () => {
        interval && clearInterval(interval);
      };
    }
  }, [
    pingInterval,
    lastHeartbeat,
    socketIoConnected,
    isTabActive,
    timeBetweenPings,
  ]);

  const update: (
    action:
      | UpdateAction
      | { type: "Build game state"; actionHistory: UpdateActionHistory }
      | { type: "Rebuild game state"; action: UpdateAction },
  ) => void = (action) => {
    const buildGameStateStartTime = Date.now();
    switch (action.type) {
      case "Build game state":
        buildGameState(action.actionHistory);

        console.debug(
          Date.now(),
          `Building game state took: ${Date.now() - buildGameStateStartTime}ms`,
        );
        break;
      case "Rebuild game state":
        reBuildGameState(action.action);

        console.debug(
          `RE-building game state took: ${Date.now() - buildGameStateStartTime}ms`,
        );
        break;
      default:
        console.debug("Updating state with event", {
          number: action.orderRank,
          type: action.type,
          payload: action.payload,
          diff: action.receivedTimestamp
            ? action.timestamp - action.receivedTimestamp
            : "???",
        });
        updateGameState(action);
        break;
    }
  };

  const consumeSocketIoMessage = (message: SocketIoMessage) => {
    switch (message[0]) {
      case "[SERVER] BROADCAST ACTION": {
        const messageContent: UpdateAction = {
          ...message[1],
          receivedTimestamp: Date.now(),
        };

        switch (messageContent.type) {
          case "[Undoable] Undo last action":
          case "[Undoable] Redo previous action":
          case "[Challenges] Challenge success":
            {
              update({
                type: "Rebuild game state",
                action: messageContent,
              });
            }
            break;
          default:
            update(messageContent);
            break;
        }
        break;
      }
      case "[SERVER] PULL ACTION HISTORY RESPONSE": {
        const newActionHistory = message[1].actionHistory.reduce(
          (collector, current) => ({
            ...collector,
            [current.orderRank]: current,
          }),
          {} as UpdateActionHistory,
        );

        console.debug(
          "SAMS Version: ",
          (newActionHistory[0].payload as InitializeMatchPayload)?.scoreVersion,
        );
        update({
          type: "Build game state",
          actionHistory: newActionHistory,
        });
        break;
      }
    }
  };

  if (window) {
    // @ts-ignore
    window["triggerUpdate"] = () => {
      const currentIndex =
        // @ts-ignore
        (window["currentAction"] as number) || lastAction.orderRank;
      // @ts-ignore
      const actions = window["allActions"] as UpdateAction[];
      const nextAction = actions[currentIndex + 1];
      if (nextAction) {
        consumeSocketIoMessage(["[SERVER] BROADCAST ACTION", nextAction]);
        // @ts-ignore
        window["currentAction"] = currentIndex + 1;
        return `Fired update for ${currentIndex + 1} (${nextAction.type}). Next action: ${actions[currentIndex + 2]?.type}`;
      } else {
        return `No action for index ${currentIndex + 1} found`;
      }
    };
  }

  return <>{children}</>;
};

export default SocketIoWrapper;
